import { createSlice, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';

import { ModuleComment } from 'shared/typings/moduleComments';
import { BrkfstAction } from 'shared/typings/redux';
import { Revision } from 'shared/typings/revisions';
import { ModuleSubmissionStatus, ReviewModule } from 'shared/typings/submissions';

type ReviewModuleState = {
  reviewModulesList: ReviewModule[]; // Modules in REVIEW status
  revisionModulesList: ReviewModule[]; // Modules in REVISION status
};

const initialState: ReviewModuleState = {
  reviewModulesList: [],
  revisionModulesList: [],
};

type ReviewModuleAction<T = ReviewModuleState> = BrkfstAction<T>;

// Helper function to find a module in either array efficiently
const findModuleInState = (state: ReviewModuleState, moduleId: number): ReviewModule | undefined => {
  return (
    state.reviewModulesList.find((module) => module.id === moduleId) ||
    state.revisionModulesList.find((module) => module.id === moduleId)
  );
};

export const reviewModulesSlice = createSlice<ReviewModuleState, SliceCaseReducers<ReviewModuleState>>({
  name: 'reviewModules',
  initialState,
  reducers: {
    reviewModulesLoaded: (state, action) => {
      state.reviewModulesList = action.payload.data;
    },
    revisionModulesLoaded: (state, action) => {
      state.revisionModulesList = action.payload.data;
    },
    reviewModuleAdded: (state, action: ReviewModuleAction<ReviewModule>) => {
      state.reviewModulesList.push(action.payload.data);
    },
    reviewModuleStatusUpdated: (state, action: ReviewModuleAction<ReviewModule>) => {
      const moduleId = action.payload.data.id;
      const newStatus = action.payload.data.status;

      // Remove module from both arrays first
      state.reviewModulesList = state.reviewModulesList.filter((module) => module.id !== moduleId);
      state.revisionModulesList = state.revisionModulesList.filter((module) => module.id !== moduleId);

      // Add to appropriate array based on new status
      if (newStatus === ModuleSubmissionStatus.REVIEW) {
        state.reviewModulesList.push(action.payload.data);
      } else if (newStatus === ModuleSubmissionStatus.REVISION) {
        state.revisionModulesList.push(action.payload.data);
      }
      // If status is neither REVIEW nor REVISION, module is removed from both arrays
    },
    revisionAdded: (
      state,
      action: PayloadAction<{
        data: Revision;
        requestData: { moduleStatus: ModuleSubmissionStatus };
      }>,
    ) => {
      const {
        data: { submissionModuleId },
        requestData: { moduleStatus },
      } = action.payload;

      // Find the module in either array to preserve its data
      const existingModule = findModuleInState(state, submissionModuleId);

      if (existingModule) {
        // Remove module from both arrays first
        state.reviewModulesList = state.reviewModulesList.filter((module) => module.id !== submissionModuleId);
        state.revisionModulesList = state.revisionModulesList.filter((module) => module.id !== submissionModuleId);

        // Update the module with the new revision and status
        const updatedModule = {
          ...existingModule,
          status: moduleStatus,
          revisions: [action.payload.data, ...(existingModule.revisions || [])],
        };

        // Add to appropriate array based on new status
        if (moduleStatus === ModuleSubmissionStatus.REVISION) {
          state.revisionModulesList.push(updatedModule);
        } else if (moduleStatus === ModuleSubmissionStatus.REVIEW) {
          state.reviewModulesList.push(updatedModule);
        }
      }
    },
    moduleCommentAdded: (state, action: ReviewModuleAction<ModuleComment>) => {
      const subModule = state.reviewModulesList.find(
        (reviewModule) => reviewModule.id === action.payload.data.submissionModuleId,
      );

      if (subModule) {
        subModule.comments = [...(subModule.comments || []), action.payload.data];
      }
    },
    moduleCommentUpdated: (state, action: ReviewModuleAction<ModuleComment>) => {
      const subModule = state.reviewModulesList.find(
        (reviewModule) => reviewModule.id === action.payload.data.submissionModuleId,
      );

      if (subModule) {
        subModule.comments = subModule.comments?.map((comment) => {
          if (comment.id === action.payload.data.id) {
            return action.payload.data;
          }
          return comment;
        });
      }
    },
    moduleCommentDeleted: (state, action: ReviewModuleAction<ModuleComment>) => {
      const subModule = state.reviewModulesList.find(
        (reviewModule) => reviewModule.id === action.payload.data.submissionModuleId,
      );

      if (subModule) {
        subModule.comments = subModule.comments?.filter((comment) => comment.id !== action.payload.data.id);
      }
    },
    revisionUpdated: (state, action: ReviewModuleAction<Revision>) => {
      const subModule = findModuleInState(state, action.payload.data.submissionModuleId);

      if (subModule) {
        subModule.revisions = subModule.revisions?.map((revision) => {
          if (revision.id === action.payload.data.id) {
            // Use the complete updated revision data from the API
            return action.payload.data;
          }
          return revision;
        });
      }
    },
    revisionDeleted: (state, action: ReviewModuleAction<{ revision: Revision }>) => {
      const subModule = findModuleInState(state, action.payload.data.revision.submissionModuleId);

      if (subModule) {
        // Remove the deleted revision
        subModule.revisions = subModule.revisions?.filter(
          (revision) => revision.id !== action.payload.data.revision.id,
        );
      }
    },
  },
});

export const {
  moduleCommentAdded,
  moduleCommentDeleted,
  moduleCommentUpdated,
  reviewModulesLoaded,
  revisionModulesLoaded,
  reviewModuleStatusUpdated,
  revisionAdded,
  revisionUpdated,
  revisionDeleted,
  reviewModuleAdded,
} = reviewModulesSlice.actions;

export const getReviewModules = (state) => state.reviewModules.reviewModulesList;
export const getRevisionModules = (state) => state.reviewModules.revisionModulesList;
export const getAllModules = (state) => [
  ...state.reviewModules.reviewModulesList,
  ...state.reviewModules.revisionModulesList,
];
export const getReviewModule = (id?: number) => (state) => {
  if (!id) return undefined;
  return (
    state.reviewModules.reviewModulesList.find((module) => module.id === id) ||
    state.reviewModules.revisionModulesList.find((module) => module.id === id)
  );
};
